import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, MoreVertical, Copy } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { useApp } from "../context/AppContext";
import { api, errMsg, useFetch } from "../lib/api";
import { timeAgo, longDate } from "../lib/fmt";
import { PageHeader, Section, Loading } from "../components/bits";
import { InviteSheet } from "../components/InviteSheet";

const INV = { pending: ["En attente", "bg-amber-100 text-amber-800"], accepted: ["Acceptée", "bg-emerald-100 text-emerald-700"], expired: ["Expirée", "bg-slate-100 text-slate-500"], revoked: ["Révoquée", "bg-red-100 text-red-700"] };

function MemberRow({ m, reload }) {
  const { sp, isOwner, isManager } = useApp();
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`member-row-${m.user_id}`}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 font-bold text-teal-800">{m.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{m.name}</div>
        <div className="text-xs text-slate-500">
          {m.role_label}{m.status === "disabled" && <span className="ml-1 font-bold text-red-600">· Désactivée</span>}
          {isManager && m.last_login && <span> · vue {timeAgo(m.last_login)}</span>}
        </div>
        {isManager && m.phone && <a href={`tel:${m.phone}`} className="text-xs font-semibold text-teal-700">{m.phone}</a>}
      </div>
      {isOwner && m.role !== "owner" && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button className="rounded-lg p-2.5 hover:bg-slate-100" data-testid={`member-menu-${m.user_id}`} aria-label="Actions"><MoreVertical className="h-5 w-5" /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            {m.role === "nurse"
              ? <DropdownMenuItem className="min-h-[44px]" data-testid="member-make-co-btn" onClick={() => act(() => api.post(sp(`/members/${m.user_id}/role`), { role: "co" }), "Désignée co-responsable")}>Désigner comme co-responsable</DropdownMenuItem>
              : <DropdownMenuItem className="min-h-[44px]" data-testid="member-remove-co-btn" onClick={() => act(() => api.post(sp(`/members/${m.user_id}/role`), { role: "nurse" }), "Statut co-responsable retiré")}>Retirer le statut co-responsable</DropdownMenuItem>}
            <DropdownMenuItem className="min-h-[44px]" data-testid="member-toggle-status-btn" onClick={() => act(() => api.post(sp(`/members/${m.user_id}/status`), { status: m.status === "active" ? "disabled" : "active" }), "Statut modifié")}>
              {m.status === "active" ? "Désactiver" : "Réactiver"}
            </DropdownMenuItem>
            <DropdownMenuItem className="min-h-[44px] text-red-600" data-testid="member-remove-btn" onClick={() => window.confirm(`Retirer ${m.name} de la structure ?`) && act(() => api.delete(sp(`/members/${m.user_id}`)), "Membre retiré")}>Retirer de la structure</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function Invitations({ reloadKey }) {
  const { sp } = useApp();
  const { data, reload } = useFetch(sp(`/invitations?k=${reloadKey}`));
  if (!data?.length) return null;
  const revoke = async (id) => { try { await api.post(sp(`/invitations/${id}/revoke`)); reload(); toast.success("Invitation révoquée"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section title="Invitations">
      <div className="card divide-y divide-slate-100">
        {data.map((i) => (
          <div key={i.id} className="flex items-center gap-2 px-4 py-3" data-testid="invitation-row">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{i.label || "Invitation"}{i.accepted_by_name ? ` → ${i.accepted_by_name}` : ""}</div>
              <div className="text-xs text-slate-500">Créée {timeAgo(i.created_at)} · expire {longDate(i.expires_at.slice(0, 10)).toLowerCase()}</div>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${INV[i.status][1]}`} data-testid="invitation-status">{INV[i.status][0]}</span>
            {i.status === "pending" && (
              <>
                <button className="p-2 text-slate-500" onClick={() => { navigator.clipboard.writeText(i.url); toast.success("Lien copié"); }} aria-label="Copier"><Copy className="h-4 w-4" /></button>
                <button className="text-xs font-bold text-red-600" onClick={() => revoke(i.id)} data-testid="invitation-revoke-btn">Révoquer</button>
              </>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function SupportRequests() {
  const { sp } = useApp();
  const { data, reload } = useFetch(sp("/support-requests"));
  if (!data?.length) return null;
  const decide = async (r, accept) => { try { await api.post(sp(`/support-requests/${r.id}/decide`), { accept }); toast.success(accept ? "Accès autorisé" : "Décision enregistrée"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card mb-6 border-2 border-amber-300 p-4" data-testid="support-requests-card">
      {data.map((r) => (
        <div key={r.id} className="space-y-2">
          <div className="font-bold">Le support AVELIS demande l'accès à votre espace</div>
          <p className="text-sm text-slate-600">Motif : {r.reason}<br />Lecture seule, {r.duration_min} minutes, toutes les consultations sont journalisées.</p>
          {r.status === "pending" ? (
            <div className="flex gap-2"><button className="btn-primary flex-1" onClick={() => decide(r, true)} data-testid="support-accept-btn">Autoriser</button><button className="btn-ghost flex-1" onClick={() => decide(r, false)} data-testid="support-refuse-btn">Refuser</button></div>
          ) : <button className="btn-ghost w-full" onClick={() => decide(r, false)} data-testid="support-revoke-btn">{r.status === "active" ? "Mettre fin à l'accès" : "Retirer l'autorisation"}</button>}
        </div>
      ))}
    </div>
  );
}

export default function Team() {
  const { sp, isOwner, isManager, isSupport } = useApp();
  const { data, reload } = useFetch(sp("/members"));
  const [open, setOpen] = useState(false);
  const [k, setK] = useState(0);
  const group = (r) => (data || []).filter((m) => m.role === r);
  return (
    <div data-testid="team-page">
      <PageHeader title="Mon équipe" back="/" />
      {isManager && !isSupport && <SupportRequests />}
      {isOwner && <button className="btn-primary mb-6 w-full !min-h-[56px] text-lg" onClick={() => setOpen(true)} data-testid="invite-member-btn"><UserPlus className="h-5 w-5" /> Inviter une infirmière</button>}
      {!data ? <Loading /> : [["owner", "Responsable principal"], ["co", "Co-responsables"], ["nurse", "Infirmières"]].map(([r, t]) => (
        <Section key={r} title={`${t} (${group(r).length})`}>
          <div className="card divide-y divide-slate-100">
            {group(r).length === 0 && <p className="px-4 py-3 text-sm text-slate-400">Personne</p>}
            {group(r).map((m) => <MemberRow key={m.user_id} m={m} reload={reload} />)}
          </div>
        </Section>
      ))}
      {isOwner && <Invitations reloadKey={k} />}
      <InviteSheet open={open} onOpenChange={setOpen} onCreated={() => setK(k + 1)} />
    </div>
  );
}
